import { useId, useState } from "react";
import { useI18n } from "../i18n";
import {
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  canGeneratePassword,
  generatePassword,
} from "../lib/password";
import type { PasswordOptions } from "../lib/password";

type CharsetOption = "lowercase" | "uppercase" | "digits" | "symbols" | "excludeAmbiguous";

const CHARSET_OPTIONS: readonly CharsetOption[] = ["lowercase", "uppercase", "digits", "symbols", "excludeAmbiguous"];

/**
 * The options panel behind "Generate password". Options are held by the
 * caller so they survive the panel closing and reopening; the password goes
 * straight to `onGenerate` and is never kept here.
 */
export function PasswordGenerator({
  options,
  onOptionsChange,
  onGenerate,
}: {
  options: PasswordOptions;
  onOptionsChange: (options: PasswordOptions) => void;
  onGenerate: (password: string) => void;
}) {
  const { m } = useI18n();
  const id = useId();
  const valid = canGeneratePassword(options);
  /**
   * What the length box shows while it is being typed in. Clamping on every
   * keystroke would turn the "3" of "32" into 8 before the "2" arrives, so
   * the box lets a too-short value stand until it loses focus. A too-long
   * one can't become valid by typing more, so it is capped at once, and
   * anything but digits is dropped (a number input otherwise takes "e", "."
   * and "-").
   */
  const [lengthDraft, setLengthDraft] = useState<string | null>(null);

  function set<K extends keyof PasswordOptions>(key: K, value: PasswordOptions[K]) {
    onOptionsChange({ ...options, [key]: value });
  }

  function clampLength(n: number): number {
    return Math.min(PASSWORD_MAX_LENGTH, Math.max(PASSWORD_MIN_LENGTH, Math.round(n)));
  }

  function typeLength(raw: string) {
    const digits = raw.replace(/\D/g, "");
    if (digits !== "" && Number(digits) > PASSWORD_MAX_LENGTH) {
      setLengthDraft(String(PASSWORD_MAX_LENGTH));
      set("length", PASSWORD_MAX_LENGTH);
      return;
    }
    setLengthDraft(digits);
    const n = Number(digits);
    if (digits !== "" && n >= PASSWORD_MIN_LENGTH) set("length", n);
  }

  function commitLength() {
    if (lengthDraft === null) return;
    const n = Number(lengthDraft);
    if (lengthDraft !== "" && Number.isFinite(n)) set("length", clampLength(n));
    setLengthDraft(null);
  }

  return (
    <div className="generator-panel">
      <div className="generator-length">
        <label className="field-label" htmlFor={`${id}-length`}>
          {m.create.generator.length}
        </label>
        <input
          type="range"
          className="generator-range"
          min={PASSWORD_MIN_LENGTH}
          max={PASSWORD_MAX_LENGTH}
          value={options.length}
          onChange={(e) => set("length", clampLength(Number(e.target.value)))}
          aria-label={m.create.generator.length}
        />
        <input
          id={`${id}-length`}
          className="input-plain generator-length-input"
          type="number"
          min={PASSWORD_MIN_LENGTH}
          max={PASSWORD_MAX_LENGTH}
          value={lengthDraft ?? options.length}
          onChange={(e) => typeLength(e.target.value)}
          onBlur={commitLength}
          onKeyDown={(e) => {
            // The panel sits inside the create form; Enter here means "done
            // with this box", not "create the secret".
            if (e.key === "Enter") {
              e.preventDefault();
              commitLength();
            }
          }}
        />
      </div>

      <div className="generator-checks">
        {CHARSET_OPTIONS.map((key) => (
          <label key={key} className="generator-check">
            <input type="checkbox" checked={options[key]} onChange={(e) => set(key, e.target.checked)} />
            <span>{m.create.generator[key]}</span>
          </label>
        ))}
      </div>

      {!valid && <p className="error-text">{m.create.generator.needCharset}</p>}

      <button
        type="button"
        className="btn btn-secondary"
        disabled={!valid}
        onClick={() => onGenerate(generatePassword(options))}
      >
        {m.create.generator.generate}
      </button>
      <p className="field-hint">{m.create.generator.note}</p>
    </div>
  );
}
